import { useEffect, useState } from 'react';

import {
    type GetToursResponse,
    TOUR_SERVICE_API_LIMIT,
    useGetTourServiceToursAfterQuery,
    useGetTourServiceToursQuery,
} from '#api/tourservice/tourService.slice.ts';
import type { Tour } from '#domain/model/Tour.ts';

const mergeToursById = (currentTours: Tour[], incomingTours: Tour[]) => {
    if (currentTours.length === 0) {
        return incomingTours;
    }

    const mergedTours = new Map(currentTours.map(tour => [tour.id, tour]));
    incomingTours.forEach(tour => {
        mergedTours.set(tour.id, tour);
    });

    return Array.from(mergedTours.values());
};

const updatePaginationState = (response: GetToursResponse, apiLimit: number) => {
    const shouldFetchMore = response.tours.length === apiLimit && response.cursor;

    if (!shouldFetchMore) {
        return {
            afterCursor: undefined,
            skipAfterQuery: true,
        };
    }

    return {
        afterCursor: response.cursor,
        skipAfterQuery: false,
    };
};

export const useFetchTours = (apiLimit = TOUR_SERVICE_API_LIMIT) => {
    const [tours, setTours] = useState<Tour[]>([]);
    const [afterCursor, setAfterCursor] = useState<string | undefined>(undefined);
    const [skipAfterQuery, setSkipAfterQuery] = useState(true);

    const {
        isLoading: isLoadingInitialTours,
        data: firstToursPage,
        error: toursError,
        refetch: refetchInitialTours,
        isFetching: isFetchingInitialTours,
    } = useGetTourServiceToursQuery(
        { limit: apiLimit },
        {
            pollingInterval: 5 * 60 * 1000,
            skipPollingIfUnfocused: true,
        }
    );

    const {
        data: toursAfterPage,
        isSuccess: isSuccessAfter,
        isError: isErrorAfter,
    } = useGetTourServiceToursAfterQuery(
        { limit: apiLimit, cursor: afterCursor ?? '' },
        { skip: skipAfterQuery || !afterCursor }
    );

    const handleFirstPageResponse = (response: GetToursResponse) => {
        setTours(response.tours);

        const { afterCursor: nextCursor, skipAfterQuery: shouldSkipAfterQuery } = updatePaginationState(
            response,
            apiLimit
        );
        setAfterCursor(nextCursor);
        setSkipAfterQuery(shouldSkipAfterQuery);
    };

    const handleAfterPageResponse = (response: GetToursResponse) => {
        setTours(currentTours => mergeToursById(currentTours, response.tours));

        const { afterCursor: nextCursor, skipAfterQuery: shouldSkipAfterQuery } = updatePaginationState(
            response,
            apiLimit
        );
        setAfterCursor(nextCursor);
        setSkipAfterQuery(shouldSkipAfterQuery);
    };

    const refetchTours = async () => {
        try {
            const { data } = await refetchInitialTours();
            if (data) {
                handleFirstPageResponse(data);
            }
        } catch (error) {
            console.error('Error fetching tours form tourservice:', error);
        }
    };

    useEffect(() => {
        if (!firstToursPage) {
            setTours([]);
            setAfterCursor(undefined);
            setSkipAfterQuery(true);
            return;
        }

        handleFirstPageResponse(firstToursPage);
    }, [firstToursPage]);

    useEffect(() => {
        if (!isSuccessAfter || !toursAfterPage) {
            return;
        }

        handleAfterPageResponse(toursAfterPage);
    }, [isSuccessAfter, toursAfterPage]);

    useEffect(() => {
        if (isErrorAfter) {
            setAfterCursor(undefined);
            setSkipAfterQuery(true);
        }
    }, [isErrorAfter]);

    return {
        tours,
        isLoadingInitialTours,
        isFetchingTours: isFetchingInitialTours,
        toursError,
        refetchTours,
    };
};
